/** Workflows P6 (M26, DEC-099): the four steps of the default workflows (Notify team members, Route the handoff, Assign the lead's owner, Send to webhook endpoints). Merged into `engage.workflows`. */
export const workflowsSystem = {
  steps: {
    notify_team: "Notify team members",
    route_handoff: "Route the handoff",
    assign_lead_owner: "Assign the lead's owner",
    send_to_subscribed_endpoints: "Send to webhook endpoints",
  },
  summaries: {
    notify_team: "{{who}}: {{what}}",
    route_agent_settings: "By each assistant's handoff settings",
    route_team: "To the {{name}} team",
    route_user: "To {{name}}",
    owner_workspace_setting: "By the leads routing setting",
    owner_round_robin: "Take turns among eligible members",
    owner_user: "To {{name}}",
    send_to_subscribed_endpoints: "Every event the endpoints subscribe to",
  },
  incomplete: {
    recipient: "Choose who is told",
    notifyTitle: "Write the title of the notice",
    routeTeam: "Choose the team",
    routeUser: "Choose the person",
    ownerUser: "Choose the person",
  },
  fields: {
    olderStep: "This is an older step. Convert it to Notify team members to choose who is told, by their own notification settings.",
    convertStep: "Convert to Notify team members",
  },
  system: {
    notify: {
      category: "Kind of notice",
      categoryHint: "Members choose in Settings → Notifications how each kind reaches them.",
      categories: {
        handoff_requested: "Handoff",
        source_processed: "Knowledge sync finished",
        source_failed: "Knowledge sync failed",
        knowledge_gap: "Knowledge gap",
        lead_assigned: "Lead assigned",
        new_lead: "New lead",
        new_booking: "New booking",
        workflow: "Workflow",
        ticket_new: "New ticket",
        ticket_assigned: "Ticket assigned",
        ticket_client_replied: "Client replied",
        ticket_mention: "Mention in a ticket",
        ticket_sla: "Ticket SLA",
        ticket_csat_low: "Low rating",
        ticket_returned_to_queue: "Ticket back in the queue",
      },
      who: "Who is told",
      recipient: "Recipient {{n}}",
      target: "Tell",
      targets: {
        assignee: "The person the conversation is assigned to",
        handoff_audience: "Whoever the handoff goes to",
        queue_audience: "Whoever handles the team's queue",
        reminder_audience: "The people who should reply, and the team lead",
        returned_audience: "The team the conversation returned to",
        team: "A team",
        team_of_conversation: "The conversation's team",
        team_lead: "The team lead",
        team_mailbox: "The team's shared mailbox",
        lead_owner: "The lead's owner",
        leads_mailbox: "The leads mailbox",
        gap_assignee: "The person the gap is assigned to",
        source_uploader: "The person who added the source",
        owners: "Owners",
        owners_admins: "Owners and Admins",
        owners_admins_for_agent: "Owners and Admins who can see the assistant's inbox",
        workspace_owner: "The workspace owner",
        permission: "Everyone with a permission",
        user: "One person",
        ticket_audience: "Whoever the ticket goes to",
        team_of_ticket: "The ticket's team",
        mentioned: "The people mentioned in the note",
      },
      teamNamed: "The {{name}} team",
      permissionNamed: "Everyone with {{permission}}",
      permission: "Permission",
      permissionHint: "Everyone who holds it (for the assistant's inbox, in that assistant's scope).",
      delivery: "How it reaches them",
      deliveryHint: "A direct notice reaches the person even if they turned this kind off. Use it only where it always did.",
      deliveries: { preferences: "By their notification settings", direct: "Directly, whatever their settings" },
      addRecipient: "Add recipient",
      removeRecipient: "Remove recipient {{n}}",
      maxRecipients: "A step tells at most {{max}} groups.",
      excludeActor: "Leave out the member who did it",
      channels: "Channels",
      channelOptions: { in_app: "In the app", email: "E-mail" },
      channelsAll: "Each member's own settings decide.",
      channelsCapped: "Only these channels, and only where the member allows them.",
      channelsFromSource: "The channels follow the knowledge source's own notification setting.",
      outOfHours: "Outside office hours",
      outOfHoursHint: "Applies when the handoff arrives while the office is closed.",
      outOfHoursRules: { same: "Send the same way", in_app_only: "In the app only" },
      standardShort: "Standard message",
    },
    route: {
      note: "Decides who a new handoff goes to, when it starts. It works only in this default workflow; in your own workflows use Assign.",
      mode: "Route to",
      modes: { agent_settings: "Each assistant's handoff settings", team: "Always a team", user: "Always one person" },
      roundRobin: "Inside the team",
      roundRobinHint: "Whether the handoff goes to one member of the team or to the whole team.",
      roundRobins: { team_setting: "Follow the team's setting", on: "Take turns between members", off: "The whole team" },
      agentSettingsHint: "Each assistant keeps its own default team or person in its handoff settings.",
    },
    owner: {
      note: "Decides who owns a new lead, when it is captured. It works only in this default workflow; in your own workflows use Update lead.",
      mode: "Give the lead to",
      modes: { workspace_setting: "Whoever the leads routing setting says", round_robin: "Members in turn", user: "Always one person" },
      workspaceSettingHint: "Change the rule itself in Settings → Leads routing.",
    },
    endpoints: {
      body: "Sends each event to the webhook endpoints that subscribe to it, signed, with retries and the delivery log.",
      hint: "It has nothing to set here. Each endpoint chooses its own events.",
      link: "Open webhook settings",
    },
  },
} as const;
